/**
 * Estado da camada de fumaça (Zustand). Registra o codec de URL `u` (de
 * "fUmaça": o `f` já é dos focos) no import — `ui/modules.tsx` importa este
 * arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { IndiceFumaca } from './api';

/** A camada começa desligada, como as outras camadas extras. */
export const DEFAULT_VISIVEL = false;
export const DEFAULT_OPACIDADE = 1;
export const LIMITES_OPACIDADE = { min: 0.2, max: 1, step: 0.05 } as const;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

export interface Situacao {
  estado: EstadoCarga;
  erro: string | null;
}

/**
 * A fumaça no quadro exibido:
 * `ok` na tela · `carregando` baixando (fica a anterior) · `sem` nenhum
 * quadro da NOAA a até 10 min deste horário · `falhou` o arquivo não veio.
 */
export type EstadoNoQuadro = 'ok' | 'carregando' | 'sem' | 'falhou';

export interface FumacaNoQuadro {
  estado: EstadoNoQuadro;
  /** Quadro da NOAA na tela (ou pedido), epoch ms. */
  t: number | null;
}

export interface Progresso {
  baixados: number;
  total: number;
  falhas: number;
}

export interface SmokeState {
  visivel: boolean;
  opacidade: number;
  situacao: Situacao;
  indice: IndiceFumaca | null;
  noQuadro: FumacaNoQuadro;
  progresso: Progresso;

  setVisivel: (on: boolean) => void;
  setOpacidade: (o: number) => void;
}

export const useSmokeStore = create<SmokeState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  opacidade: DEFAULT_OPACIDADE,
  situacao: { estado: 'idle', erro: null },
  indice: null,
  noQuadro: { estado: 'sem', t: null },
  progresso: { baixados: 0, total: 0, falhas: 0 },

  setVisivel: (visivel) => set({ visivel }),
  setOpacidade: (opacidade) => set({ opacidade }),
}));

/** `u=1` liga a camada; `o0.6` = opacidade, só quando difere do padrão. Ex.: `u=1o0.6`. */
export function encodeSmoke(s: Pick<SmokeState, 'visivel' | 'opacidade'>): string | null {
  if (!s.visivel) return null;
  return s.opacidade !== DEFAULT_OPACIDADE ? `1o${s.opacidade}` : '1';
}

export function decodeSmoke(value: string): Partial<Pick<SmokeState, 'visivel' | 'opacidade'>> | null {
  if (!value || value[0] !== '1') return null;
  const out: Partial<Pick<SmokeState, 'visivel' | 'opacidade'>> = { visivel: true };
  const o = /o(\d*\.?\d+)/.exec(value);
  if (o) {
    const n = Number(o[1]);
    out.opacidade = Number.isFinite(n)
      ? Math.min(LIMITES_OPACIDADE.max, Math.max(LIMITES_OPACIDADE.min, n))
      : DEFAULT_OPACIDADE;
  }
  return out;
}

registerUrlCodec({
  key: 'u',
  encode: () => encodeSmoke(useSmokeStore.getState()),
  decode: (value) => {
    const s = decodeSmoke(value);
    if (s) useSmokeStore.setState(s);
  },
  subscribe: (onChange) =>
    useSmokeStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.opacidade !== b.opacidade) onChange();
    }),
});
